import React, { useEffect, useState } from 'react';
import { normalizeStructuredSections } from './siteContent';

const paragraphStyle = {
  margin: '0 0 1.1rem 0',
  fontSize: '1.08rem',
  lineHeight: '1.75',
  color: '#374151',
};

function paragraphsToText(paragraphs = []) {
  return paragraphs.join('\n\n');
}

function bulletsToText(bullets = []) {
  return bullets.join('\n');
}

function parseParagraphs(text = '') {
  return text
    .split(/\n\s*\n/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function parseBullets(text = '') {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function renderTextWithLinks(text) {
  const parts = String(text).split(/(admin@attackyouradventure\.com)/);

  return parts.map((part, index) => {
    if (part === 'admin@attackyouradventure.com') {
      return (
        <a key={index} href="mailto:admin@attackyouradventure.com">
          admin@attackyouradventure.com
        </a>
      );
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function StructuredSectionEditor({ section, index, onChange, onBlur }) {
  const label = section.title?.trim() || `Section ${index + 1}`;

  return (
    <div className="structured-page-editor-section">
      <label className="structured-page-editor-label">
        Section title <span className="structured-page-editor-hint">(optional)</span>
        <input
          type="text"
          value={section.title || ''}
          onChange={(e) => onChange({ ...section, title: e.target.value })}
          onBlur={onBlur}
          placeholder="e.g. How Rentals Work"
          className="structured-page-editor-title"
        />
      </label>

      <label className="structured-page-editor-label">
        Paragraphs
        <span className="structured-page-editor-hint">Separate paragraphs with a blank line</span>
        <textarea
          value={paragraphsToText(section.paragraphs)}
          onChange={(e) => onChange({ ...section, paragraphs: parseParagraphs(e.target.value) })}
          onBlur={onBlur}
          rows={6}
          className="structured-page-editor-textarea"
        />
      </label>

      <label className="structured-page-editor-label">
        Bullet points <span className="structured-page-editor-hint">(optional — one per line)</span>
        <textarea
          value={bulletsToText(section.bullets)}
          onChange={(e) => onChange({ ...section, bullets: parseBullets(e.target.value) })}
          onBlur={onBlur}
          rows={5}
          className="structured-page-editor-textarea"
        />
      </label>

      <label className="structured-page-editor-label">
        Paragraphs after bullets
        <span className="structured-page-editor-hint">(optional — separate paragraphs with a blank line)</span>
        <textarea
          value={paragraphsToText(section.paragraphsAfterBullets)}
          onChange={(e) => onChange({ ...section, paragraphsAfterBullets: parseParagraphs(e.target.value) })}
          onBlur={onBlur}
          rows={4}
          className="structured-page-editor-textarea"
        />
      </label>

      <p className="structured-page-editor-section-name">{label}</p>
    </div>
  );
}

function EditableStructuredPageContent({ sections, isAdmin, isLoading, onSave, style = {} }) {
  const [draftSections, setDraftSections] = useState(() => normalizeStructuredSections(sections));

  useEffect(() => {
    setDraftSections(normalizeStructuredSections(sections));
  }, [sections]);

  const persist = (nextSections) => {
    const normalized = normalizeStructuredSections(nextSections);
    setDraftSections(normalized);
    onSave(normalized);
  };

  if (isLoading) {
    return <p style={{ color: '#6b7280', ...style }}>Loading page content...</p>;
  }

  if (isAdmin) {
    return (
      <div className="structured-page-editor" style={style}>
        <p className="structured-page-editor-intro">
          Each block is a section on the page. Add a title for subsection headings, paragraphs for body
          copy, and bullet lines for lists.
        </p>
        {draftSections.map((section, index) => (
          <StructuredSectionEditor
            key={index}
            section={section}
            index={index}
            onChange={(updatedSection) => {
              const nextSections = [...draftSections];
              nextSections[index] = updatedSection;
              setDraftSections(nextSections);
            }}
            onBlur={() => persist(draftSections)}
          />
        ))}
        <div className="structured-page-editor-actions">
          <button
            type="button"
            className="structured-page-editor-add"
            onClick={() => persist([...draftSections, { title: '', paragraphs: [], bullets: [], paragraphsAfterBullets: [] }])}
          >
            Add section
          </button>
          {draftSections.length > 1 && (
            <button
              type="button"
              className="structured-page-editor-remove"
              onClick={() => persist(draftSections.slice(0, -1))}
            >
              Remove last section
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="structured-page-content" style={style}>
      {draftSections.map((section, index) => {
        const hasTitle = Boolean(section.title?.trim());
        const hasParagraphs = section.paragraphs?.some((paragraph) => paragraph.trim());
        const hasBullets = section.bullets?.some((bullet) => bullet.trim());
        const hasParagraphsAfterBullets = section.paragraphsAfterBullets?.some((paragraph) => paragraph.trim());

        if (!hasTitle && !hasParagraphs && !hasBullets && !hasParagraphsAfterBullets) return null;

        return (
          <article key={index} className="structured-page-section">
            {hasTitle && <h3 className="structured-page-section-title">{section.title}</h3>}
            {section.paragraphs
              ?.filter((paragraph) => paragraph.trim())
              .map((paragraph) => (
                <p key={paragraph} style={paragraphStyle}>
                  {renderTextWithLinks(paragraph)}
                </p>
              ))}
            {hasBullets && (
              <ul className="structured-page-bullets">
                {section.bullets
                  .filter((bullet) => bullet.trim())
                  .map((bullet) => (
                    <li key={bullet}>{renderTextWithLinks(bullet)}</li>
                  ))}
              </ul>
            )}
            {section.paragraphsAfterBullets
              ?.filter((paragraph) => paragraph.trim())
              .map((paragraph) => (
                <p key={paragraph} style={paragraphStyle}>
                  {renderTextWithLinks(paragraph)}
                </p>
              ))}
          </article>
        );
      })}
    </div>
  );
}

export default EditableStructuredPageContent;